import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Truck, Pencil } from 'lucide-react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

interface Veiculo {
  id: number;
  emitente_id: number;
  placa: string;
  renavam: string | null;
  tara: number | null;
  cap_kg: number | null;
  uf: string | null;
  descricao: string | null;
  ativo: boolean;
}

async function listarVeiculos(emitenteId?: number): Promise<Veiculo[]> {
  const params: Record<string, string | number> = {};
  if (emitenteId) params.emitente_id = emitenteId;
  const { data } = await api.get<Veiculo[]>('/veiculos', { params });
  return data;
}

export default function VeiculosListPage() {
  const [search] = useSearchParams();
  const emitenteId = search.get('emitente') ? Number(search.get('emitente')) : undefined;
  const [busca, setBusca] = useState('');
  const q = useQuery({
    queryKey: ['veiculos', emitenteId],
    queryFn: () => listarVeiculos(emitenteId),
  });

  const filtered = useMemo(() => {
    const all = q.data ?? [];
    if (!busca.trim()) return all;
    const t = busca.toLowerCase();
    return all.filter(
      (v) =>
        v.placa.toLowerCase().includes(t) ||
        (v.descricao || '').toLowerCase().includes(t) ||
        (v.renavam || '').toLowerCase().includes(t),
    );
  }, [q.data, busca]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow">Cadastro</div>
          <h1 className="h-display text-xl text-slate-950 mt-0.5 dark:text-white">Veículos</h1>
        </div>
        <Link
          to={emitenteId ? `/veiculos/novo?emitente=${emitenteId}` : '/veiculos/novo'}
          className="btn-primary text-xs !py-1.5"
        >
          <Plus className="h-3.5 w-3.5" /> Novo veículo
        </Link>
      </div>

      <div className="card-surface p-3">
        <input
          className="field-input"
          placeholder="Buscar por placa, RENAVAM, descrição..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      {q.isLoading && (
        <div className="py-10 grid place-items-center">
          <LoadingSpinner />
        </div>
      )}

      {!q.isLoading && filtered.length === 0 && (
        <div className="card-surface p-10 text-center text-sm text-neutral-600 dark:text-neutral-400">
          Nenhum veículo cadastrado.
        </div>
      )}

      {!q.isLoading && filtered.length > 0 && (
        <div className="card-surface overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-neutral-500 border-b border-slate-200 dark:border-white/10">
              <tr>
                <th className="py-2 px-3">Placa</th>
                <th className="py-2 px-3">UF</th>
                <th className="py-2 px-3">RENAVAM</th>
                <th className="py-2 px-3">Tara (kg)</th>
                <th className="py-2 px-3">Cap. (kg)</th>
                <th className="py-2 px-3">Descrição</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-white/5">
              {filtered.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50 dark:hover:bg-white/[0.02]">
                  <td className="py-2 px-3 mono font-semibold">
                    <span className="inline-flex items-center gap-1.5">
                      <Truck className="h-3.5 w-3.5 text-amber-700 dark:text-amber-400" />
                      {v.placa}
                    </span>
                  </td>
                  <td className="py-2 px-3">{v.uf || '-'}</td>
                  <td className="py-2 px-3 mono text-xs text-neutral-600 dark:text-neutral-400">{v.renavam || '-'}</td>
                  <td className="py-2 px-3 tab-nums">{v.tara ?? '-'}</td>
                  <td className="py-2 px-3 tab-nums">{v.cap_kg ?? '-'}</td>
                  <td className="py-2 px-3 max-w-xs truncate" title={v.descricao || ''}>
                    {v.descricao || '-'}
                  </td>
                  <td className="py-2 px-3">
                    {v.ativo ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Inativo</span>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <Link to={`/veiculos/${v.id}`} className="toolbar-btn text-[10px] !py-0.5 !px-1.5">
                      <Pencil className="h-3 w-3" /> Editar
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
